import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';
import { breakpoint, theme } from '../../theme';
import { Heading } from '../typography';
import { CrossIcon } from '../../assets';

export type ModalProps = PropsWithChildren<{
  isLoading?: boolean;
  onClose: () => void;
  title?: string;
  titleVariant?: TitleVariant;
  titlePosition?: TitlePosition;
  className?: string;
  curvedCorner?: 'top' | 'bottom';
}>;

type TitleProps = {
  variant: TitleVariant;
  titlePosition: TitlePosition;
};
type StyledModalProps = {
  curvedCorner?: 'top' | 'bottom';
};

type TitleVariant = 'primary' | 'secondary';

type TitlePosition = 'left' | 'center' | 'right';

const StyledModal = styled.div<StyledModalProps>`
  background-color: ${theme.colors.primary.white};
  border-radius: ${({ curvedCorner }) =>
    curvedCorner === 'top' ? '10px 10px 0 0' : '0 0 10px 10px'};
  padding: ${theme.spacings[4].rem};
  width: 100%;

  ${breakpoint('sm')`
    border-radius: 2px;
    min-width: 300px;
    margin: 0 30px;
    max-width: 780px;
    padding: ${theme.spacings[7].rem};
    width: fit-content;
  `}

  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
`;

const Title = styled(Heading)<TitleProps>`
  color: ${theme.colors.primary.graysBlue};
  margin: ${`0 ${theme.spacings[2].rem} ${theme.spacings[5].rem} ${theme.spacings[4].rem}`};
  margin: ${({ titlePosition }) =>
    titlePosition === 'left' &&
    `0 ${theme.spacings[2].rem} ${theme.spacings[5].rem} 0`};
  text-align: ${({ titlePosition }) =>
    titlePosition === 'center'
      ? 'center'
      : titlePosition === 'left'
      ? 'left'
      : 'right'};
  text-transform: uppercase;
  width: 100%;

  ${breakpoint('sm')<TitleProps>`
    ${({ variant, titlePosition }) =>
      variant === 'primary' &&
      `
      margin: 0 ${theme.spacings[4].rem} ${theme.spacings[5].rem} 0;
      text-align: ${
        titlePosition === 'center'
          ? 'center'
          : titlePosition === 'left'
          ? 'left'
          : 'right'
      };
    `}
  `}
`;

const Cross = styled(CrossIcon)`
  cursor: pointer;
  fill: ${theme.colors.neutrals.solidGrays[1]};
  height: 16px;
  width: 16px;
`;

export const Modal: React.FC<ModalProps> = ({
  children,
  isLoading = false,
  onClose,
  title,
  titleVariant = 'primary',
  titlePosition = 'center',
  className,
  curvedCorner = 'top',
}) => {
  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    setIsDataLoading(isLoading);
  }, [isLoading]);

  return (
    <StyledModal className={className} curvedCorner={curvedCorner}>
      {isDataLoading ? (
        //TODO: add loading spinner
        <Header>
          <div>Loading</div>
          {onClose && <Cross onClick={onClose} />}
        </Header>
      ) : (
        <>
          <Header>
            <Title
              size={5}
              variant={titleVariant}
              titlePosition={titlePosition}
            >
              {title}
            </Title>
            {onClose && <Cross onClick={onClose} />}
          </Header>
          {children}
        </>
      )}
    </StyledModal>
  );
};
